import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Theater, CalendarDays, Landmark, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getMe } from "@/lib/socialpulseOidc";
import { MovieCard } from "@/components/MovieCard";
import { PageToolbar, UnsplashCredit, VerifiedBadge, usePageTranslation } from "@/components/PageToolbar";

export default function CityDetail() {
  const { id } = useParams();
  const [city, setCity] = useState(null);
  const [me, setMe] = useState(null);
  const [memory, setMemory] = useState({ author: "", body: "", theater: "" });

  const load = () => api.get(`/cities/${id}`).then((r) => setCity(r.data));

  useEffect(() => {
    window.scrollTo(0, 0);
    setCity(null);
    load();
    getMe().then(setMe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitMemory = async (e) => {
    e.preventDefault();
    if (!memory.body.trim()) return toast.error("Write your memory first");
    await api.post(`/cities/${id}/memories`, memory);
    toast.success("Memory added to the city archive");
    setMemory({ author: "", body: "", theater: "" });
    load();
  };

  const blocks = useMemo(
    () => (city ? { tagline: city.tagline, overview: city.overview, history: city.screen_history } : null),
    [city]
  );
  const { lang, tr, busy } = usePageTranslation(blocks, `city-${id}`);

  if (!city) return <main className="max-w-7xl mx-auto px-6 py-24 text-center font-display text-2xl text-[#6b5d51]">Loading…</main>;

  const speakText = `${city.name}, ${city.country}. ${tr("tagline", city.tagline)}. ${tr("overview", city.overview)} ${tr("history", city.screen_history)}`;

  return (
    <main data-testid="city-detail-page">
      <section className="bg-[#171210] text-[#f6f1e7] relative overflow-hidden">
        {city.photo && <img src={city.photo.url} alt={city.photo.alt} className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <p className="eyebrow" style={{ color: "#e8b96a" }}>City Cinema Home · {city.country}</p>
          <h1 data-testid="city-name" className="font-display font-bold text-4xl sm:text-6xl mt-2">{city.name}</h1>
          <p className="font-script text-2xl text-[#e8b96a] mt-2">{tr("tagline", city.tagline)}</p>
          <p className="mt-5 text-lg leading-relaxed max-w-3xl text-[#e9e2d4]">{tr("overview", city.overview)}</p>
          <div className="mt-6">
            <PageToolbar speakText={speakText} busy={busy} lang={lang} />
          </div>
          {city.photo && <UnsplashCredit photo={city.photo} className="mt-4 !text-[#a99e8d]" />}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display font-bold text-2xl mb-5 rule-thick pt-4 flex items-center gap-2">
            <Theater className="w-5 h-5 text-[#7d1a1a]" /> Theaters & Picture Houses
          </h2>
          <div className="space-y-3">
            {city.theaters.map((t) => (
              <div key={t.name} data-testid={`theater-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-[#fbf8f1] border border-[#d8ccb8] p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-display font-semibold text-lg">{t.name}</p>
                  <span className="font-mono-label text-[9px] tracking-[0.18em] uppercase bg-[#efe7d7] px-2 py-1">{t.type}</span>
                </div>
                <p className="text-sm text-[#6b5d51] mt-2">{t.note}</p>
              </div>
            ))}
          </div>

          <h2 className="font-display font-bold text-2xl mt-10 mb-4 rule-thick pt-4 flex items-center gap-2">
            <Landmark className="w-5 h-5 text-[#7d1a1a]" /> Local Screen History
          </h2>
          <p className="text-lg leading-relaxed text-[#2b241d] bg-[#efe7d7] p-5 border-l-4 border-[#7d1a1a]">{tr("history", city.screen_history)}</p>

          <h2 className="font-display font-bold text-2xl mt-10 mb-2 rule-thick pt-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#7d1a1a]" /> Theater Memories
          </h2>
          <p className="text-sm text-[#6b5d51] mb-4">Community-shared memories of moviegoing in {city.name}. Personal recollections — clearly separate from the verified record.</p>
          <form data-testid="memory-form" onSubmit={submitMemory} className="bg-[#fbf8f1] border-2 border-[#1c1512] p-5 space-y-3">
            <textarea
              data-testid="memory-body-input"
              value={memory.body}
              onChange={(e) => setMemory({ ...memory, body: e.target.value })}
              placeholder={`A screening, a seat, a marquee night in ${city.name} you'll never forget…`}
              rows={3}
              className="w-full bg-transparent border border-[#d8ccb8] focus:border-[#7d1a1a] outline-none p-3 text-sm"
            />
            <div className="flex flex-wrap gap-3 items-center">
              {me ? (
                <span data-testid="posting-as-label" className="text-sm text-[#6b5d51]">
                  Posting as <span className="font-medium text-[#1c1512]">{me.name}</span> <VerifiedBadge />
                </span>
              ) : (
                <input
                  data-testid="memory-author-input"
                  value={memory.author}
                  onChange={(e) => setMemory({ ...memory, author: e.target.value })}
                  placeholder="Your name (optional)"
                  className="bg-transparent border border-[#d8ccb8] focus:border-[#7d1a1a] outline-none px-3 py-2 text-sm"
                />
              )}
              <input
                data-testid="memory-theater-input"
                value={memory.theater}
                onChange={(e) => setMemory({ ...memory, theater: e.target.value })}
                placeholder="Theater name (optional)"
                className="bg-transparent border border-[#d8ccb8] focus:border-[#7d1a1a] outline-none px-3 py-2 text-sm"
              />
              <button data-testid="memory-submit-button" type="submit" className="bg-[#7d1a1a] text-[#f6f1e7] px-5 py-2 font-medium hover:bg-[#5c1220] transition-colors">
                Share Memory
              </button>
            </div>
          </form>
          <div className="mt-4 space-y-3">
            {(city.memories || []).map((m) => (
              <div key={m.id} data-testid={`memory-${m.id}`} className="bg-[#efe7d7] p-4 border-l-4 border-[#b06810]">
                <p className="text-[#2b241d] italic">“{m.body}”</p>
                <p className="font-mono-label text-[10px] text-[#8a7c6d] mt-2 flex items-center gap-1.5">
                  — {m.author} {m.verified && <VerifiedBadge />}{m.theater ? ` · ${m.theater}` : ""} · {new Date(m.created).toLocaleDateString()}
                </p>
              </div>
            ))}
            {(!city.memories || city.memories.length === 0) && (
              <p data-testid="memories-empty-state" className="text-sm text-[#8a7c6d]">No memories shared yet — be the first to add one.</p>
            )}
          </div>
        </div>

        <aside className="space-y-8">
          <div>
            <h3 className="font-display font-bold text-xl mb-4 rule-thick pt-4 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#7d1a1a]" /> Festivals
            </h3>
            <ul className="space-y-2">
              {city.festivals.map((f) => (
                <li key={f} className="bg-[#fbf8f1] border border-[#d8ccb8] px-4 py-3 text-sm font-medium">{f}</li>
              ))}
            </ul>
          </div>
          <Link to={`/country/${city.country_code}`} data-testid="city-country-link" className="block bg-[#7d1a1a] text-[#f6f1e7] p-5 hover:bg-[#5c1220] transition-colors">
            <p className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#e8b96a]">National Cinema Home</p>
            <p className="font-display font-bold text-2xl mt-1">{city.country} →</p>
          </Link>
        </aside>
      </section>

      {city.movies?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
          <h2 className="font-display font-bold text-2xl mb-6 rule-thick pt-4">Essential Films from {city.country}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {city.movies.map((m) => <MovieCard key={m.id} movie={m} />)}
          </div>
        </section>
      )}
    </main>
  );
}
